import classnames from 'classnames2';
import React from 'react';

import { IBaseProps } from '../utils';

import { Description } from '../Description';

import radioStyles from './RadioButton.module.scss';
import { RadioIndicatorSVG } from './RadioIndicatorSVG';

export interface IRadioButtonProps extends IBaseProps {
  /** Determines if radio button is disabled. */
  disabled?: boolean;
  /** Adds a description below the radio button label. */
  description?: React.ReactNode;
  /** The text label that describes the purpose of this radio button option to the user */
  label?: string;
  /** Provides a value for the name attribute of the radio button,
   * which should be used to group it with other radio buttons
   */
  name?: string;
  /** Adds a tooltip when the radio button is hovered. */
  tooltip?: string;
  /** Determines if radio button is checked. */
  selected?: boolean;
  /** Sets the value of the radio button. */
  value?: string;
  /** A function that is called whenever the user tries to update the radio button input control value,
   *  except when disabled is set to true
   */
  onChange?(value: string): any;
}

function createRadioButton(displayName, labelSizeStyle) {
  const RadioButtonKind: React.FC<IRadioButtonProps> = (props) => {
    const idName = props.name ? props.name.replace(/\s/g, '') : '';
    const idLabel = props.label ? props.label.replace(/\s/g, '') : '';
    const radioLabel = `radioLabel-${idName}-${idLabel}`;

    return (
      <label
        className={classnames(props.className, radioStyles.label, labelSizeStyle, {
          [radioStyles.labelSelected]: props.selected,
          [radioStyles.labelDisabled]: props.disabled
        })}
        data-test={props.dataTest}
        title={props.tooltip}
      >
        <input
          type="radio"
          name={props.name}
          className={radioStyles.input}
          checked={props.selected}
          aria-checked={props.selected}
          aria-labelledby={radioLabel}
          disabled={props.disabled}
          onChange={() => {
            if (!props.disabled) {
              props.onChange(props.value);
            }
          }}
        />
        <RadioIndicatorSVG
          containerClassName={radioStyles.indicator}
          backgroundClassName={radioStyles.indicatorBackground}
          outerRingClassName={radioStyles.indicatorOuterRing}
          innerRingClassName={radioStyles.indicatorInnerRing}
        />
        <div className={radioStyles.labelAndDescriptionWrapper} id={radioLabel}>
          {props.label}
          {props.description &&
            (typeof props.description === 'string' ? (
              <Description>{props.description}</Description>
            ) : (
              props.description
            ))}
        </div>
      </label>
    );
  };

  RadioButtonKind.displayName = displayName;

  return RadioButtonKind;
}

const DefaultRadioButton = createRadioButton('RadioButton.Default', radioStyles.labelDefault);

export const RadioButton = Object.assign(DefaultRadioButton, {
  Default: DefaultRadioButton,
  Small: createRadioButton('RadioButton.Small', radioStyles.labelSmall)
});
